import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import * as Services from '@shared/core/services';

import * as State from '@shared/state';

import * as selectors from '@shared/state/selectors';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { combineLatest, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';


@Injectable()
export class WizzardCollapseService {
    private _pagesRegistry: Map<number, OLO.Components.IWizzardStepThroughRegDataObj> = new Map();
    private _change$: BehaviorSubject<{ index: number; page: OLO.Models.IMenuFlowPage; }> = new BehaviorSubject(null);
    private _activePageIndex$: Subject<number> = new Subject();

    constructor(
        private _store: Store<State.IStateShared>,
        private _wizzardService: Services.WizzardService
    ) { }

    public destroy(): void {
        this._wizzardService.registerStepThroughWizzardChange(null);
    }

    public registerPage(menuFlowPageId: number, regData: OLO.Components.IWizzardStepThroughRegDataObj): void {
        this._pagesRegistry.set(menuFlowPageId, regData);
    }

    public unregisterPage(menuFlowPageId: number): void {
        this._pagesRegistry.delete(menuFlowPageId);
    }

    public stepChangeRegister(isExpanded: boolean, menuFlowPageIdentifier: number, index: number) {
        const regElem = this._pagesRegistry.get(menuFlowPageIdentifier);

        this._wizzardService.registerStepThroughWizzardChange({
            isExpanded,
            isLast: index + 1 === this._pagesRegistry.size,
            isFirst: index === 0,
            element: regElem.element,
            totalItems: this._pagesRegistry.size,
            menuFlowPage: regElem.menuFlowPage,
            index,
        });
    }

    public shouldCollapse$(page: OLO.Models.IMenuFlowPage): Observable<{
        isComplete: boolean;
        totalSelected: number;
        index: number;
        errors: boolean;
        previousPage: OLO.Models.IMenuFlowPage;
        isMaxedOut: boolean;
    }> {
        /* Decide if current page should collapse or not - this method should be subscribed in collapse page. Additional logic might be provided */

        return this._store.pipe(
            select(selectors.isWizzardPageQuantityValid(page))
        )
            .pipe(
                combineLatest(
                    this.changes,
                ),
                distinctUntilChanged(),
                filter(([isComplete, changes]) => changes !== null && isComplete !== null),
                combineLatest(
                    this._store
                        .pipe(
                            select(selectors.getWizzardErrors),
                            map(errors => errors.find(error => error.pageIdentifier === page.PageIdentifier) ? true : false)
                        ),
                    this._store
                        .pipe(
                            select(selectors.getWizzardMenuFlowPageItemsTotalQuantity(page.PageIdentifier))
                        ),
                    this._store.pipe(
                        select(selectors.isWizzardPageQuantityMaxedOut(page))
                    ),
                ),
                map(([[isComplete, changes], errors, totalSelected, isMaxedOut]) => ({
                    isComplete,
                    totalSelected,
                    index: changes.index,
                    errors,
                    previousPage: changes.page,
                    isMaxedOut,
                }))
            );
    }

    public shouldExpandCalculated$(page: OLO.Models.IMenuFlowPage, currIndex: number): Observable<boolean> {
        return this.shouldCollapse$(page)
            .pipe(
                switchMap(({ isComplete, totalSelected, index, errors, previousPage, isMaxedOut }) => this._store.pipe(
                    select(selectors.isWizzardPageQuantityMaxedOut(previousPage)),
                    map(isPreviousMaxedOut => {
                        if (errors) return true;

                        const shouldCollapse = !isPreviousMaxedOut || index === currIndex && isComplete;
                        if (shouldCollapse) {
                            return false;
                        }

                        const shouldExpand = (!isComplete || isComplete && totalSelected === 0) && index + 1 === currIndex;
                        if (shouldExpand) {
                            return true;
                        }

                        return false;
                    })
                ))
            );
    }

    public emitChange(obj: { index: number; page: OLO.Models.IMenuFlowPage; }): void {
        this._change$.next(obj);
    }

    public reset(): void {
        this._change$.next(null);
    }

    public setActivePageIndex(index: number): void {
        this._activePageIndex$.next(index);
    }

    public get activePageIndex(): Subject<number> {
        return this._activePageIndex$;
    }

    public get changes(): BehaviorSubject<{ index: number; page: OLO.Models.IMenuFlowPage; }> {
        return this._change$;
    }
}
